import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import omit from 'lodash.omit';

import Button from '../../../../../../Button';
import TrashIcon from '../../../../../../../icons/TrashIcon';
import CustomControl from '../../../../../../CustomControl';
import CloneIcon from '../../../../../../../icons/CloneIcon';
import { isCardElementEditable } from '../../../../../../../../services/cards/components/AccountCard/helpers';
import { DESIGN_MODES } from '../../../../../constants';
import CardDesignerCardElementMeasurementControls from './components/CardDesignerCardElementMeasurementControls';
import { CARD_ELEMENT_TYPE } from '../../../../../../../../services/cards/constants';
import CardDesignerCardElementBorderControl from '../../../CardDesignerCardElementControls/components/CardDesignerCardElementBorderControl';
import CardDesignerCardElementPhotoControls from '../../../CardDesignerCardElementPhoto/CardDesignerCardElementPhotoControls';
import CardDesignerCardElementBoxShadowControls from '../../../CardDesignerCardElementControls/components/CardDesignerCardElementBoxShadowControls';
import CardDesignerCardElementOpacityControls from '../../../CardDesignerCardElementControls/components/CardDesignerCardElementOpacityControls';

const propTypes = {
  className: PropTypes.string,
  element: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onCardElementCreate: PropTypes.func.isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
  onCardElementDelete: PropTypes.func.isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
  designMode: PropTypes.oneOf(Object.values(DESIGN_MODES)).isRequired,
  cardTemplate: PropTypes.object,
};

const CardDesignerCardElementCommonControls = ({
  className,
  element,
  onCardElementCreate,
  onCardElementUpdate,
  onCardElementDelete,
  onElementConfigurationChange,
  designMode,
  cardTemplate,
  isLogo,
}) => {
  const classes = classNames({
    'card-designer-card-element-common-controls': true,
    [className]: !!className,
  });

  const { cardElement, cardElementConfiguration } = element;
  const {
    asset_image: assetImage,
    corner_radius: elementBorderRadius,
    border: elementBorder,
  } = cardElementConfiguration ?? {};

  return (
    <div className={classes}>
      <div className="card-designer-card-element-common-control-slot d-flex">
        {[
          CARD_ELEMENT_TYPE.PHOTO,
          CARD_ELEMENT_TYPE.TEXT,
          CARD_ELEMENT_TYPE.BARCODE,
        ].includes(cardElement?.type) && (
          <CardDesignerCardElementMeasurementControls
            element={element}
            onCardElementUpdate={onCardElementUpdate}
            cardOrientation={cardTemplate.orientation}
            cardPrintSize={
              cardTemplate.print_size ?? cardTemplate.print_product?.print_size
            }
          />
        )}
        {cardElement?.type !== CARD_ELEMENT_TYPE.BARCODE && !isLogo && (
          <Fragment>
            <Button
              btnStyle="reset"
              className="ml-1"
              onClick={() => {
                const { frame } = cardElement;
                const cloneFrame = {
                  ...omit(frame, '__typename'),
                  x: Math.round((frame.x + 0.025) * 1e3) / 1e3,
                  y: Math.round((frame.y + 0.025) * 1e3) / 1e3,
                };

                if (frame.x + frame.width > 0.9) {
                  cloneFrame.x = Math.round((frame.x - 0.025) * 1e3) / 1e3;
                }
                if (frame.y + frame.height > 0.9) {
                  cloneFrame.y = Math.round((frame.y - 0.025) * 1e3) / 1e3;
                }

                onCardElementCreate({
                  cardElementType: cardElement.type,
                  input: {
                    frame: cloneFrame,
                    z_index: cardElement.z_index + 1,
                  },
                  cardElementConfiguration: {
                    ...omit(cardElementConfiguration, [
                      'id',
                      'card_element',
                      'asset_image',
                      'photo',
                      'is_pro_editable',
                      'label',
                      '__typename',
                    ]),
                    border: elementBorder
                      ? omit(elementBorder, ['__typename'])
                      : undefined,
                    corner_radius: elementBorderRadius,
                    existing_asset_image_id: assetImage?.id,
                  },
                });
              }}
              data-for="clone-element"
              data-place="bottom"
              data-effect="solid"
              data-tip
            >
              <CloneIcon size={22} color="#798588" />
            </Button>
            <ReactTooltip id="clone-element" className="tooltip">
              Clone Element
            </ReactTooltip>
          </Fragment>
        )}
        {cardElement?.type === CARD_ELEMENT_TYPE.PHOTO && !isLogo && (
          <CardDesignerCardElementBorderControl
            onElementConfigurationChange={onElementConfigurationChange}
            element={element}
          />
        )}
      </div>
      {[CARD_ELEMENT_TYPE.PHOTO, CARD_ELEMENT_TYPE.TEXT].includes(
        cardElement?.type,
      ) &&
        !isLogo && (
          <Fragment>
            <CardDesignerCardElementOpacityControls
              onElementConfigurationChange={onElementConfigurationChange}
              element={element}
            />
            <CardDesignerCardElementBoxShadowControls
              onElementConfigurationChange={onElementConfigurationChange}
              element={element}
            />
          </Fragment>
        )}
      {cardElement?.type === CARD_ELEMENT_TYPE.PHOTO && !isLogo && (
        <CardDesignerCardElementPhotoControls
          element={element}
          onElementConfigurationChange={onElementConfigurationChange}
          className="ml-1"
        />
      )}

      {!assetImage &&
        !isLogo &&
        designMode !== DESIGN_MODES.COLLAGES &&
        cardElement?.type !== CARD_ELEMENT_TYPE.BARCODE && (
          <div
            className="card-designer-card-element-common-control-slot"
            data-for="user-editable"
            data-place="bottom"
            data-effect="solid"
            data-tip
          >
            <CustomControl
              type="checkbox"
              label="User Editable"
              id="is_editable"
              name="is_editable"
              readOnly
              checked={isCardElementEditable(element)}
              onChange={() => {
                onElementConfigurationChange({
                  element,
                  config: {
                    is_editable: !isCardElementEditable(element),
                  },
                });
              }}
            />
            <ReactTooltip id="user-editable" className="tooltip">
              Turning this off will prevent user customization of this element.
              e.g. if you want to keep text or a logo unmodified.
            </ReactTooltip>
          </div>
        )}

      <div className="card-designer-card-element-common-control-slot">
        <Button
          btnStyle="reset"
          onClick={() => onCardElementDelete(element.cardElement.id)}
          data-for="element-delete"
          data-place="bottom"
          data-effect="solid"
          data-tip
        >
          <TrashIcon color="#fe5851" size={22} />
        </Button>
        <ReactTooltip id="element-delete" className="tooltip">
          Delete Element
        </ReactTooltip>
      </div>
    </div>
  );
};

CardDesignerCardElementCommonControls.propTypes = propTypes;

export default CardDesignerCardElementCommonControls;
